import Header from './components/Header';
import Table from './components/Table';
import ContentPhases from './components/ContentPhases';
import Evidences from './components/Evidences';
import Signatures from './components/Signatures';
import { useTranslation } from 'react-i18next';
import ReportTranslationService from '../../../../../../../../../../services/ReportTranslationService';
import { useMemo } from 'react';
import { PDFViewer } from '@react-pdf/renderer';
import ReportTemplate from '../../../../../../../../../../services/ReportTemplate';

// não sei se eu poderia ter usado isso aqui. talvez deveríamos colocar companyInfo no estado global?
import useTemplateState from '../../../../../../../../../../Template/atoms/variables';
import logo from '../../../../../../../../../../Template/images/logo.svg';
import moment from 'moment';
import './style.css';
import InstrumentTable from './components/InstrumentTable/InstrumentTable';

/**
 * Componente principal do formulário de relatório
 * Gerencia a exibição e edição de relatórios com suporte a versionamento
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.editing - Indica se o relatório está em modo de edição
 * @param {Object} props.report - Dados do relatório
 * @param {Function} props.setReport - Função para atualizar o relatório
 */
const ReportForms = ({ editing, report, handlePhaseValueChange }) => {
  const { t, i18n } = useTranslation();

  const { companyInfo } = useTemplateState();
  // Usar useMemo para cachear a tradução e recalcular quando o idioma ou relatório mudar
  const translatedReport = useMemo(() => {
    return ReportTranslationService.translateReport(report, i18n.language);
  }, [report, i18n.language]);

  const generalInfo = useMemo(
    () =>
      [
        {
          title: t('contractor') + ': ',
          value: report.employee.properties.find((p) => p.key === 'company').value,
          valid: true,
        },
        {
          title: t('date') + ':',
          // Test execution date — never updatedAt, which moves on any workflow touch (signatures, edits)
          value: report.createdAt ? moment(report.createdAt).format('DD/MM/YYYY') : 'N/A',
          valid: true,
        },
        { title: t('location') + ':', value: report.subject?.location?.name, valid: true },
        { title: t('name') + ':', value: report.subject?.name, valid: true },
        { title: t('token') + ':', value: report.token, valid: true },
        { title: t('attempt') + ':', value: report.attempt, valid: true },
        report.subject.properties.find((p) => p.key === 'origin')?.value && {
          title: t('origin') + ':',
          value: report.subject.properties.find((p) => p.key === 'origin')?.value,
          valid: true,
        },
        report.subject.properties.find((p) => p.key === 'destiny')?.value && {
          title: t('destiny') + ':',
          value: report.subject.properties.find((p) => p.key === 'destiny')?.value,
          valid: true,
        },
      ].filter(Boolean),
    [t, report],
  ); // Dependências do generalInfo

  if (!translatedReport) return null;

  // Unified visualization with PDF when not editing
  if (!editing) {
    return (
      <div className='reportBody' style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <PDFViewer style={{ width: '100%', height: 'calc(100vh - 180px)' }} showToolbar>
          <ReportTemplate report={translatedReport} logoUrl={companyInfo?.logo ?? logo} />
        </PDFViewer>
      </div>
    );
  }

  // Editing mode (existing editable DOM forms)
  return (
    <div className='reportBody'>
      <Header reportLogo={companyInfo?.logo ?? logo} report={report} />
      <div className='report-content'>
        <Table title={t('general_tab')} content={generalInfo} />
        {report.test?.pt?.plan?.instrument && (
          <InstrumentTable instrument={report.test?.pt?.plan?.instrument} />
        )}

        <ContentPhases
          phases={report.phase}
          editing={editing}
          onValueChange={handlePhaseValueChange}
          validation={report.test?.pt?.validation}
        />
        {report.test?.pt?.validation?.acceptanceCriteria && (
          <Table
            title={t('validationCriteria')}
            content={[
              report.test?.pt?.validation?.acceptanceCriteria?.absoluteMaxError && {
                title: t('absoluteMaxError'),
                value: report.test?.pt?.validation?.acceptanceCriteria?.absoluteMaxError,
                valid: true,
                index: 0,
              },
              report.test?.pt?.validation?.acceptanceCriteria?.relativeMaxError && {
                title: t('relativeMaxError'),
                value: report.test?.pt?.validation?.acceptanceCriteria?.relativeMaxError,
                valid: true,
                index: 1,
              },
              report.test?.pt?.validation?.acceptanceCriteria?.acceptance && {
                title: t('criteria'),
                value: report.test?.pt?.validation?.acceptanceCriteria?.acceptance,
                valid: true,
                index: 2,
              },
              report.test?.pt?.validation?.acceptanceCriteria?.result && {
                title: t('result'),
                value: report.test?.pt?.validation?.acceptanceCriteria?.result,
                valid: true,
                index: 3,
              },
            ].filter(Boolean)}
          />
        )}
        <Evidences phases={report.phase} />
        <Signatures report={translatedReport} />
      </div>
    </div>
  );
};

export default ReportForms;
